"use client";

import React from "react";
import { ArrowRight, Calendar, Compass, MapPin, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const highlights = [{
  label: "7 Nights at Sea",
  icon: Calendar
}, {
  label: "World-Class Entertainment",
  icon: Sparkles
}, {
  label: "Open Bar & VIP Experiences",
  icon: Compass
}, {
  label: "Global LGBTQ+ Community",
  icon: Users
}];
export default function TheWorldAwaits() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="225442" id="the-world-awaits" className="relative min-h-screen flex items-center justify-center bg-background text-foreground overflow-hidden dark py-20 lg:py-24">
      {/* Background Image with Responsive Tags */}
      <div className="absolute inset-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687938/img/atlantis_cruise_caribbean_sunset_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687938/img/atlantis_cruise_caribbean_sunset_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687938/img/atlantis_cruise_caribbean_sunset_hero.png-wvc-sizes" alt="Atlantis Cruise luxury ship sailing Caribbean waters at golden sunset" className="w-full h-full object-cover object-center" loading="eager" />
      </div>

      {/* Dark Gradient Overlay for Readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80 backdrop-blur-[1px]" />

      {/* Main Content */}
      <div ref={ref} className={`relative z-10 flex flex-col items-center text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        {/* Eyebrow Label */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-white/10 border border-white/20 backdrop-blur-md mb-6">
          <span className="w-2 h-2 rounded-lg bg-[#d4af37] animate-pulse" />
          <span className="text-xs uppercase tracking-[0.25em] text-[#d4af37] font-default font-medium">
            Featured Experience
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight font-light tracking-wide text-foreground mb-4 drop-shadow-2xl">
          Atlantis Cruise — Caribbean 2027
        </h1>

        {/* Date Tagline */}
        <p className="text-lg sm:text-xl text-[#d4af37] font-default font-medium tracking-wide mb-6">
          March 7 – 14, 2027
        </p>

        {/* Description */}
        <p className="max-w-3xl text-sm sm:text-base md:text-lg text-foreground/85 font-default leading-relaxed mb-8">
          7-night Caribbean voyage aboard Royal Caribbean&apos;s Allure of the Seas departing Miami with stops in Nassau, Cozumel, and Roatan, featuring world-class entertainment and a global LGBTQ+ community at sea.
        </p>

        {/* Feature Highlights Badges */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-3xl mb-10">
          {highlights.map((item, i) => {
          const Icon = item.icon;
          return <div key={item.label} data-index={i} className="flex items-center justify-center gap-2 p-2.5 sm:p-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/15 text-xs text-foreground font-default">
                <Icon className="size-3.5 text-[#d4af37] shrink-0" />
                <span className="truncate">{item.label}</span>
              </div>;
        })}
        </div>

        {/* Call to Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 w-full sm:w-auto">
          <Link to="/BespokeCruisesPage" className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto h-14 px-10 text-sm sm:text-base tracking-widest uppercase rounded-lg shadow-xl hover:shadow-2xl transition-all bg-[#1a365d] text-white hover:bg-[#1a365d]/90 font-default font-semibold">
              Explore Atlantis Cruise
            </Button>
          </Link>

          <Link to="/BespokeCruisesPage" className="w-full sm:w-auto">
            <Button variant="outline" size="lg" className="w-full sm:w-auto h-14 px-10 text-sm sm:text-base tracking-widest uppercase rounded-lg border-foreground/20 text-foreground hover:bg-foreground/10 backdrop-blur-md font-default">
              View Bespoke Cruises
              <ArrowRight className="ml-3 size-5" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Bottom Information Bar & Scroll Cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10 w-full px-4 text-center">
        <div className="hidden md:flex items-center justify-center gap-6 text-[11px] uppercase tracking-[0.2em] text-foreground/70 font-default bg-black/40 backdrop-blur-md px-6 py-2 rounded-lg border border-white/10">
          <span>March 7 – 14, 2027 · 7 Nights Caribbean Adventure</span>
          <span className="text-[#d4af37]">•</span>
          <span>Departing From: Miami, Florida</span>
          <span className="text-[#d4af37]">•</span>
          <span>Destinations: Nassau, Cozumel, Roatan</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 font-default">
            Discover More
          </span>
          <div className="w-[1px] h-8 bg-primary/60 animate-pulse origin-top" />
        </div>
      </div>
    </section>;
}